import clsx from 'clsx';
import {
    Editor as PrimeEditor,
    type EditorProps as PrimeEditorProps,
} from 'primereact/editor';
import { forwardRef, type ReactNode, type Ref } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    buildRhfRules,
    type FieldLengthRule,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';

export type EditorProps = PrimeEditorProps &
    InputWrapperSharedProps & {
        name?: string;
        disabled?: boolean;
        invalid?: boolean;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
        /**
         * RHF minLength rule, only used when this input is RHF-controlled via `name`
         */
        minLength?: FieldLengthRule;
        /**
         * RHF maxLength rule, only used when this input is RHF-controlled via `name`
         */
        maxLength?: FieldLengthRule;
    };

const DEFAULT_HEADER_TEMPLATE = (
    <span className="ql-formats">
        <button type="button" className="ql-bold" aria-label="Bold" />
        <button type="button" className="ql-italic" aria-label="Italic" />
        <button type="button" className="ql-underline" aria-label="Underline" />
        <button
            type="button"
            className="ql-list"
            value="bullet"
            aria-label="Bullet List"
        />
    </span>
);

function getDefaultHeaderTemplate(props: EditorProps): ReactNode | undefined {
    if (typeof props.headerTemplate !== 'undefined') {
        return props.headerTemplate;
    }

    return DEFAULT_HEADER_TEMPLATE;
}

function EditorComponent(props: EditorProps, ref: Ref<PrimeEditor>): ReactNode {
    const headerTemplate = getDefaultHeaderTemplate(props);
    const {
        name,
        value,
        errorMessage,
        horizontal,
        labelClassName,
        bulkMode,
        className: classNameProps,
        disabled,
        invalid: invalidProps,
        readOnly: readOnlyProps,
        onBlur,
        wrapperClassName: __,
        errorClassName: ___,
        labelTooltip: ____,
        onTextChange,
        headerTemplate: _____,
        validate,
        minLength,
        maxLength,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const testId = dataTestId || name;
    const formContext = useFormContext();
    const isControlled = typeof value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'Editor',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    const isReadOnly = Boolean(disabled || readOnlyProps);
    const editorModeKey = isReadOnly ? 'readonly' : 'editable';

    if (!isRHFControlled) {
        const isInvalid = Boolean(errorMessage || invalidProps);

        return (
            <InputWrapper {...props}>
                <PrimeEditor
                    key={editorModeKey}
                    {...restProps}
                    data-testid={testId}
                    headerTemplate={headerTemplate}
                    className={clsx(
                        'p-editor-container',
                        classNameProps,
                        isInvalid && 'p-invalid',
                    )}
                    value={value ?? ''}
                    readOnly={isReadOnly}
                    onTextChange={onTextChange}
                    ref={ref}
                />
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name!}
            control={formContext!.control}
            rules={buildRhfRules({
                required: props.required,
                bulkMode,
                validate,
                minLength,
                maxLength,
            })}
            render={({ field, fieldState }) => (
                <InputWrapper
                    field={field}
                    fieldState={fieldState}
                    horizontal={horizontal}
                    labelClassName={labelClassName}
                    name={name}
                    {...props}
                >
                    <PrimeEditor
                        key={editorModeKey}
                        {...restProps}
                        data-testid={testId}
                        headerTemplate={headerTemplate}
                        className={clsx(
                            'p-editor-container',
                            classNameProps,
                            (fieldState.invalid || invalidProps) && 'p-invalid',
                        )}
                        value={field.value ?? props.value ?? ''}
                        readOnly={isReadOnly}
                        onTextChange={(event) => {
                            field.onChange(event.htmlValue ?? '');

                            if (onTextChange) {
                                onTextChange(event);
                            }
                        }}
                        ref={mergeRefs(ref, field.ref)}
                        onBlur={(event) => {
                            field.onBlur();
                            onBlur?.(event);
                        }}
                    />
                </InputWrapper>
            )}
        />
    );
}

export const Editor = forwardRef(EditorComponent);
